import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Heading,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  adminPublishReadingOrder,
  getAdminReadingOrder,
} from "../../../services/adminApi";
import { Card } from "../../auth/components/Login/Card";
import Spinner from "../../../shared/components/Spinner";
import ReadingOrderPublishForm from "../components/ReadingOrderPublishForm";

function ReadingOrderPublish() {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const readingOrderId = id;
  const { data, isLoading, isError, isSuccess } = useQuery(
    ["readingOrder", readingOrderId],
    () => getAdminReadingOrder(readingOrderId),
    {
      refetchOnWindowFocus: false,
    }
  );

  const publishMutation = useMutation((values) => {
    return adminPublishReadingOrder(readingOrderId, values);
  });

  // Handle form submit
  async function handleSubmit(values) {
    try {
      await publishMutation.mutateAsync(values);
      await queryClient.invalidateQueries(["readingOrder", readingOrderId]);
      await queryClient.invalidateQueries(["readingOrders"]);

      const isScheduled = values.publicationMode === "schedule";
      const articleNote = values.skipArticle ? " No article was created." : "";
      toast({
        title: isScheduled
          ? "Reading order scheduled."
          : "Reading order published.",
        description: isScheduled
          ? `This reading order will be released on ${values.scheduledFor}.${
              values.skipArticle ? " No article will be created." : ""
            }`
          : `Your reading order has been published.${articleNote}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      navigate("/reading-orders/all");
    } catch (error) {
      toast({
        title: "Unable to release reading order.",
        description:
          error.response?.data?.message ||
          "Please check the form and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }

  return (
    <Box
      bg={useColorModeValue("gray.50", "inherit")}
      minH="100vh"
      py="12"
      px={{ base: "4", lg: "8" }}
    >
      <Box maxW="xl" mx="auto">
        <Breadcrumb mb="3">
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/reading-orders/all">
              Reading Orders
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>Publish reading order</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <Card>
          <Heading as="h2" mb="6">
            Release reading order: {data?.data?.title}
          </Heading>
          {isLoading && <Spinner />}
          {isError && <Box>Unable to load this reading order.</Box>}
          {isSuccess && (
            <ReadingOrderPublishForm
              handleSubmit={handleSubmit}
              isLoading={publishMutation.isLoading}
              readingOrder={data?.data}
            />
          )}
        </Card>
      </Box>
    </Box>
  );
}

export default ReadingOrderPublish;
